<template>
  <div class="contact-page">
    <div class="w-full bg-white py-16 border-b border-gray-200">
      <div class="container mx-auto px-4 max-w-4xl">
        <PageBreadcrumb :paths="[
          { url: '/fr/contact', label: 'Contact' }
        ]" />
        <h1 class="text-6xl font-bold mb-8 text-black text-center">Contact</h1>
        <p class="text-xl text-gray-700 text-center max-w-6xl mx-auto">Demande, question produit ou note directe. Écrivez clairement. Nous répondons.</p>
      </div>
    </div>

    <div class="container mx-auto px-4 py-16 max-w-2xl">
      <form v-if="!formClosed" @submit.prevent="submitForm" class="space-y-6">
        <!-- Name Fields -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="firstName" class="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
            <input
              id="firstName"
              v-model="form.firstName"
              type="text"
              required
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-black bg-white"
              placeholder="Votre prénom"
            />
          </div>
          <div>
            <label for="lastName" class="block text-sm font-medium text-gray-700 mb-2">Nom</label>
            <input
              id="lastName"
              v-model="form.lastName"
              type="text"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-black bg-white"
              placeholder="Votre nom"
            />
          </div>
        </div>

        <!-- Email -->
        <div>
          <label for="email" class="block text-sm font-medium text-gray-700 mb-2">Email *</label>
          <input
            id="email"
            v-model="form.email"
            type="email"
            required
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-black bg-white"
            placeholder="votre adresse email"
          />
        </div>

        <!-- Address -->
        <div>
          <label for="address" class="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
          <textarea
            id="address"
            v-model="form.address"
            rows="3"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-black bg-white"
            placeholder="Votre adresse complète"
          ></textarea>
        </div>

        <!-- Reason -->
        <div>
          <label for="reason" class="block text-sm font-medium text-gray-700 mb-2">Raison *</label>
          <select
            id="reason"
            v-model="form.reason"
            required
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-black bg-white"
          >
            <option value="" class="text-black bg-white">Sélectionnez une raison</option>
            <option value="general" class="text-black bg-white">Demande générale</option>
            <option value="order" class="text-black bg-white">Question sur commande</option>
            <option value="product" class="text-black bg-white">Information produit</option>
            <option value="support" class="text-black bg-white">Support client</option>
            <option value="wholesale" class="text-black bg-white">Demande grossiste</option>
            <option value="press" class="text-black bg-white">Presse / Médias</option>
            <option value="other" class="text-black bg-white">Autre</option>
          </select>
        </div>

        <!-- Message -->
        <div>
          <label for="message" class="block text-sm font-medium text-gray-700 mb-2">Message *</label>
          <textarea
            id="message"
            v-model="form.message"
            rows="6"
            required
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-black bg-white"
            placeholder="Votre message..."
          ></textarea>
        </div>

        <!-- Honeypot field (hidden from users, but bots will fill it) -->
        <div style="position: absolute; left: -9999px; opacity: 0; pointer-events: none;" aria-hidden="true">
          <label for="website" style="display: none;">Website</label>
          <input
            id="website"
            v-model="form.website"
            type="text"
            name="website"
            tabindex="-1"
            autocomplete="off"
          />
        </div>

        <!-- Newsletter Subscription -->
        <div>
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.newsletter"
              type="checkbox"
              class="w-4 h-4 rounded border-2 border-gray-300 focus:ring-2 focus:ring-black focus:border-transparent"
            >
            <span class="text-sm text-gray-700">
              Je souhaite recevoir la newsletter Kendrick Paris
            </span>
          </label>
        </div>

        <!-- Submit Button -->
        <div>
          <button
            type="submit"
            :disabled="isSubmitting"
            class="w-full bg-black text-white py-4 px-6 rounded-lg font-medium hover:bg-gray-800 focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span v-if="isSubmitting">Envoi en cours...</span>
            <span v-else>Envoyer la demande</span>
          </button>
        </div>

        <!-- Success/Error Messages -->
        <div v-if="submitStatus" class="mt-4 p-4 rounded-lg" :class="submitStatus.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'">
          {{ submitStatus.message }}
        </div>
      </form>

      <div v-else class="p-6 rounded-lg border border-green-200 bg-green-50 text-green-900 text-center">
        <p class="font-medium">Demande reçue.</p>
        <p class="mt-2 text-sm text-green-800/80">Nous répondons après revue.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useToast } from '~/composables/useToast'

// Set page title
useHead({
  title: 'Contact — Kendrick Paris',
  meta: [
    { name: 'description', content: 'Demande, information produit ou message direct à Kendrick Paris.' }
  ]
})

const { showToast } = useToast()
const route = useRoute()
const signalsKit = useSignalsKit()

const form = ref({
  firstName: '',
  lastName: '',
  email: '',
  address: '',
  reason: '',
  message: '',
  newsletter: false,
  website: '' // Honeypot field
})

const isSubmitting = ref(false)
const submitStatus = ref(null)
const formClosed = ref(false)

const submitForm = async () => {
  isSubmitting.value = true
  submitStatus.value = null

  try {
    // Check honeypot field - if filled, it's likely a bot
    if (form.value.website) {
      // Silently reject without showing error to avoid bot learning
      console.log('Spam detected: honeypot field filled')
      return
    }

    // Submit contact form via API endpoint
    const response = await $fetch('/api/contact-submit', {
      method: 'POST',
      body: {
        firstName: form.value.firstName,
        lastName: form.value.lastName,
        email: form.value.email,
        address: form.value.address,
        reason: form.value.reason,
        message: form.value.message,
        newsletter: form.value.newsletter
      }
    })

    if (response.success) {
      signalsKit.trackFormGoal({
        eventName: 'contact_request',
        formType: 'contact',
        formLocation: 'fr_contact_page',
        requestType: form.value.reason || 'general',
        locale: 'fr',
        properties: {
          route_full_path: route.fullPath,
          newsletter_opt_in: Boolean(form.value.newsletter),
          has_address: Boolean(form.value.address.trim()),
          has_message: Boolean(form.value.message.trim())
        }
      })

      // Reset form
      form.value = {
        firstName: '',
        lastName: '',
        email: '',
        address: '',
        reason: '',
        message: '',
        newsletter: false,
        website: ''
      }
      
      submitStatus.value = {
        type: 'success',
        message: 'Demande reçue. Nous répondons après revue.'
      }
      formClosed.value = true
      showToast({
        type: 'success',
        title: 'Demande reçue',
        message: 'Nous répondons après revue.'
      })
    } else {
      throw new Error(response.message || 'Failed to submit form')
    }
  } catch (error) {
    console.error('Error submitting form:', error)
    submitStatus.value = {
      type: 'error',
      message: 'Impossible d’envoyer la demande pour le moment. Veuillez réessayer.'
    }
    showToast({
      type: 'error',
      title: 'Erreur',
      message: 'Envoi impossible pour le moment'
    })
  } finally {
    isSubmitting.value = false
  }
}

</script>

<style scoped>
.contact-page {
  min-height: 100vh;
}
</style>
